// Admin "Recheck with Razorpay" flow shared by Recharge History and Customer Detail.
// Asks for confirmation, calls POST /payments/recheck, and explains the outcome.
// "Preview only" runs the same check without changing anything (dryRun).
import Swal from 'sweetalert2';
import { paymentApi } from '../api/services';

const PURPLE = '#7c3aed';
const rupee = (paise) => `₹${(Number(paise || 0) / 100).toFixed(2)}`;

const describe = (r, dryRun) => {
  const attempts = Array.isArray(r.attempts) && r.attempts.length ? ` Razorpay shows ${r.attempts.length} attempt(s): ${r.attempts.join(', ')}.` : ' Razorpay shows no payment attempt yet.';
  switch (r.action) {
    case 'credited':
      return { icon: 'success', title: 'Wallet credited', text: `${rupee(r.capturedPaise)} was captured at Razorpay (${r.razorpayPaymentId}). The customer's wallet has been credited and the payment is now Success.` };
    case 'would-credit':
      return { icon: 'info', title: 'Preview: would credit', text: `${rupee(r.capturedPaise)} is captured at Razorpay (${r.razorpayPaymentId}). Run "Check & apply" to credit the wallet.` };
    case 'already-success':
      return { icon: 'info', title: 'Already credited', text: 'This payment is already Success; nothing to do.' };
    case 'marked-failed':
      return { icon: 'warning', title: 'Marked as failed', text: `No successful payment at Razorpay (${r.reason}). The payment is now Failed and any coupon has been released.` };
    case 'would-mark-failed':
      return { icon: 'info', title: 'Preview: would mark failed', text: `No successful payment at Razorpay (${r.reason}). "Check & apply" would mark it Failed.` };
    case 'still-pending':
      return { icon: 'info', title: 'Still pending', text: `No captured payment yet.${attempts} Rows are marked Failed automatically 24 hours after creation if nothing is paid.` };
    case 'no-order-yet':
      return { icon: 'info', title: 'No Razorpay order', text: 'The customer never reached Razorpay checkout for this payment, so nothing was paid.' };
    case 'authorized-not-captured':
      return { icon: 'warning', title: 'Authorized but not captured', text: `Razorpay holds the money (${r.razorpayPaymentId}) but it is not captured. Check the auto-capture setting in the Razorpay dashboard; uncaptured payments are auto-refunded after a few days.` };
    case 'amount-mismatch':
      return { icon: 'error', title: 'Amount mismatch', text: `Razorpay captured ${rupee(r.capturedPaise)} but this record is for ₹${r.rowAmount}. Not credited; please check manually.` };
    case 'refunded-not-credited':
      return { icon: 'warning', title: 'Refunded at Razorpay', text: `Payment ${r.razorpayPaymentId} was refunded, so the wallet was not credited.` };
    case 'not-razorpay':
      return { icon: 'info', title: 'Not a Razorpay payment', text: 'Only Razorpay payments can be rechecked.' };
    case 'not-found':
      return { icon: 'error', title: 'Payment not found', text: 'This payment id does not exist on the server.' };
    case 'skipped':
      return { icon: 'info', title: 'Nothing changed', text: r.reason || 'The payment changed while checking; refresh the list.' };
    case 'error':
      return { icon: 'error', title: 'Recheck failed', text: r.error || 'Unknown error' };
    default:
      return { icon: 'info', title: dryRun ? 'Preview' : 'Done', text: JSON.stringify(r) };
  }
};

/**
 * @param {{ id:number|string, amount?:string|number, userName?:string }} payment
 * @param {() => void} [afterChange] called when the server may have changed the row (not on preview)
 * @returns {Promise<object|null>} the server result, or null when cancelled
 */
export const recheckPayment = async (payment, afterChange) => {
  const who = payment.userName ? ` for ${payment.userName}` : '';
  const ask = await Swal.fire({
    title: 'Recheck with Razorpay?',
    html: `Payment <b>#${payment.id}</b>${payment.amount ? ` · ₹${payment.amount}` : ''}${who}.<br/><br/>` +
      'The server asks Razorpay about this payment right now:<br/>' +
      '• captured → wallet is credited and status becomes <b>Success</b><br/>' +
      '• nothing paid for 24+ hours → status becomes <b>Failed</b><br/>' +
      '• otherwise it stays Pending and you see why.',
    icon: 'question',
    showCancelButton: true,
    showDenyButton: true,
    confirmButtonText: 'Check & apply',
    denyButtonText: 'Preview only',
    confirmButtonColor: PURPLE,
    denyButtonColor: '#3b82f6',
    cancelButtonColor: '#64748b',
  });
  if (!ask.isConfirmed && !ask.isDenied) return null;
  const dryRun = ask.isDenied;

  Swal.fire({ title: 'Checking with Razorpay…', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
  let result;
  try {
    const res = await paymentApi.recheck({ paymentId: payment.id, dryRun });
    result = res.data || {};
  } catch (e) {
    result = { action: 'error', error: e.response?.data?.message || e.message || 'Request failed' };
  }
  const d = describe(result, dryRun);
  await Swal.fire({ ...d, confirmButtonColor: PURPLE });
  if (!dryRun && typeof afterChange === 'function') afterChange();
  return result;
};

export default recheckPayment;
